import { getOrThrow, get, set } from 'quartz/globalState/api';
import { ROOT_PACKAGE_NAME, ROOT_PACKAGE_VERSION, CURRENT_PACKAGE_NAME, CURRENT_PACKAGE_VERSION, PARENT_PACKAGE_NAME, PARENT_PACKAGE_VERSION, GRANITE_ENABLED_DESCRIPTOR, GRANITE_REMOTE_APP_DESCRIPTOR, APP_START_TIME_DESCRIPTOR } from './globalState';

/**
 * Returns the name of the root package.
 *
 * Will be the same value as getCurrentPackageName if called in the root app.
 *
 * Note: When running locally w/o running an iframed app locally, the iframed app
 * will believe itself to be the root.
 *
 * @example
 * ```ts
 * import { getRootPackageName } from 'quartz/framework/environment/service';
 *
 * // In crm-index-ui
 * console.log(getRootPackageName()); // 'crm-index-ui'
 *
 * // In object-builder-ui, iframed into crm-index-ui
 * console.log(getRootPackageName()); // 'crm-index-ui'
 * ```
 */
export const getRootPackageName = () => getOrThrow(ROOT_PACKAGE_NAME);

/**
 * Returns the version of the root package.
 *
 * Will be the same value as getCurrentPackageVersion if called in the root app.
 *
 * Note: When running locally w/o running an iframed app locally, the iframed app
 * will believe itself to be the root.
 *
 * @example
 * ```ts
 * import { getRootPackageVersion } from 'quartz/framework/environment/service';
 *
 * // In crm-index-ui
 * console.log(getRootPackageVersion()); // '1'
 *
 * // In object-builder-ui, iframed into crm-index-ui
 * console.log(getRootPackageVersion()); // '1'
 * ```
 */
export const getRootPackageVersion = () => getOrThrow(ROOT_PACKAGE_VERSION);

/**
 * Returns the name of the current package.
 *
 * @example
 * ```ts
 * import { getCurrentPackageName } from 'quartz/framework/environment/service';
 *
 * // In crm-index-ui
 * console.log(getCurrentPackageName()); // 'crm-index-ui'
 *
 * // In crm-app-auth-test-ui
 * console.log(getCurrentPackageName()); // 'crm-app-auth-test-ui'
 * ```
 */
export const getCurrentPackageName = () => getOrThrow(CURRENT_PACKAGE_NAME);

// TODO: Is `dev` when running locally something we can depend on?
// Comes from bending.package.version
// https://git.hubteam.com/HubSpot/bend-hubspot/blob/fc727e214956711c4f15c61ef9d6b62ecde9e55c/packages/bender-webpack/src/current-package-loader.ts#L8
/**
 * Returns the version of the current package.
 *
 * Will be `dev` if called when running locally.
 *
 * @example
 * ```ts
 * import { getCurrentPackageVersion } from 'quartz/framework/environment/service';
 *
 * // In crm-index-ui
 * console.log(getCurrentPackageVersion()); // '1'
 *
 * // When running crm-index-ui locally
 * console.log(getCurrentPackageVersion()); // 'dev'
 * ```
 */
export const getCurrentPackageVersion = () => getOrThrow(CURRENT_PACKAGE_VERSION);
export const getParentPackageName = () => getOrThrow(PARENT_PACKAGE_NAME);
export const getParentPackageVersion = () => getOrThrow(PARENT_PACKAGE_VERSION);
export const setParentPackage = (name, version) => {
  set(PARENT_PACKAGE_NAME, name);
  set(PARENT_PACKAGE_VERSION, version);
};
export function setIsGraniteEnabled(enabled) {
  set(GRANITE_ENABLED_DESCRIPTOR, enabled);
}
export function setIsGraniteRemoteApp(isRemote) {
  set(GRANITE_REMOTE_APP_DESCRIPTOR, isRemote);
}
export function isGraniteEnabled() {
  var _get;
  return (_get = get(GRANITE_ENABLED_DESCRIPTOR)) !== null && _get !== void 0 ? _get : false;
}
export function isGraniteRemoteApp() {
  var _get2;
  return (_get2 = get(GRANITE_REMOTE_APP_DESCRIPTOR)) !== null && _get2 !== void 0 ? _get2 : false;
}
export function setAppStartTime() {
  set(APP_START_TIME_DESCRIPTOR, performance.now());
}
export function getTimeSinceAppStartMs() {
  var _get3;
  return performance.now() - ((_get3 = get(APP_START_TIME_DESCRIPTOR)) !== null && _get3 !== void 0 ? _get3 : 0);
}